import React, { useState, useMemo } from 'react';
import CategoryCard from './CategoryCard';
import AllCategoriesModal from './AllCategoriesModal';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import { rankCategoriesByInterest } from '../../utils/userPreferences';

/**
 * CategoryDiscoverySection
 * Displays a curated, clean set of primary featured category cards on the homepage,
 * with a prominent [ View All Categories ] action opening the compact category button modal.
 */

const PRIMARY_CATEGORY_LIMIT = 8;

function CategorySkeleton() {
    return (
        <div className="im-skel-card">
            <div className="im-skel-card__media im-skeleton" style={{ height: '150px' }} />
            <div className="im-skel-card__body">
                <div className="im-skeleton im-skel-line im-skel-line--title" />
                <div className="im-skeleton im-skel-line im-skel-line--short" />
            </div>
        </div>
    );
}

function CategoryDiscoverySection({ categories, isLoading }) {
    const [isAllModalOpen, setIsAllModalOpen] = useState(false);

    // Apply personalization interest ranking if signals exist
    const rankedCategories = useMemo(() => {
        if (!categories || categories.length === 0) return [];
        return rankCategoriesByInterest(categories);
    }, [categories]);

    // Filter curated primary categories (featured or image-backed, capped at 8)
    const primaryCategories = useMemo(() => {
        if (!rankedCategories || rankedCategories.length === 0) return [];
        const withImage = rankedCategories.filter((c) => c && c.image);
        if (withImage.length >= 4) {
            return withImage.slice(0, PRIMARY_CATEGORY_LIMIT);
        }
        return rankedCategories.slice(0, PRIMARY_CATEGORY_LIMIT);
    }, [rankedCategories]);

    if (!isLoading && (!categories || categories.length === 0)) return null;

    return (
        <section className="im-section">
            <div className="im-container">
                <div className="im-section__head">
                    <div>
                        <span className="im-eyebrow">Browse by category</span>
                        <h2>What are you looking for?</h2>
                        <p className="im-section__sub">
                            Explore popular categories or view all 30 departments.
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            type="button"
                            className="im-view-all-cats-btn"
                            onClick={() => setIsAllModalOpen(true)}
                            aria-label="View all categories"
                        >
                            <WidgetsOutlinedIcon style={{ fontSize: 18 }} />
                            <span>View All Categories</span>
                        </button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="im-grid im-grid--cats" role="status" aria-busy="true">
                        <span className="im-sr-only">Loading categories</span>
                        {Array.from({ length: 4 }, (_, i) => (
                            <CategorySkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    <>
                        {primaryCategories.length > 0 && (
                            <div
                                className="im-grid im-grid--cats"
                                style={{ '--im-cat-cols': Math.min(4, primaryCategories.length) }}
                            >
                                {primaryCategories.map((category, index) => (
                                    <CategoryCard
                                        key={category.id || category._id || category.name}
                                        category={category}
                                        eager={index < 4}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Bottom action banner for quick access */}
                        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                            <button
                                type="button"
                                className="im-view-all-cats-btn"
                                onClick={() => setIsAllModalOpen(true)}
                                style={{ width: '100%', maxWidth: '420px', padding: '12px 24px' }}
                            >
                                <WidgetsOutlinedIcon style={{ fontSize: 20 }} />
                                <span>Browse All {categories?.length || 30} Categories &rarr;</span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* All Categories Interactive Modal */}
            <AllCategoriesModal
                isOpen={isAllModalOpen}
                onClose={() => setIsAllModalOpen(false)}
                categories={categories || []}
            />
        </section>
    );
}

export default CategoryDiscoverySection;
